import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
  Spinner,
  Table,
} from "reactstrap";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/plugins/monthSelect/style.css";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { requestJson } from "../../../helpers/api";
import baconLogo from "../../../assets/images/baconappen.png";
import { resolveUsuarioActual } from "./ControlStock/formatters";
import { createMonthSelectPlugin } from "./ControlStock/dateUtils";

type ServidoresOfflineResponse = {
  servidores?: string[];
};

type ResumenCostoRow = {
  tipo: "costo";
  serverOrigen: string;
  descripcion: string | null;
  idArticulo: string | null;
  familia?: string | null;
  cantidadTotal: number;
  costoPromedioSinIva: number;
  costoPromedioConIva: number;
  periodoTexto: string;
};

type DetalleCompraRow = {
  fecha: string | null;
  factura: number | null;
  nroFactura: string | null;
  origenMesCosto?: string | null;
  cantidad: number;
  costoUnitSinIva?: number;
  costoUnitConIva: number;
  importeTotal: number;
  persona: string | null;
  deposito: string | null;
  condicion?: string | null;
  tipoMovimiento?: string | null;
  fechaCarga?: string | null;
  usuario?: string | null;
};

const numberFormat = new Intl.NumberFormat("es-PY", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 3,
});

const costFormat = new Intl.NumberFormat("es-PY", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const quantityFormat = new Intl.NumberFormat("es-PY", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 4,
});

const getCurrentMonth = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
};

const parseMonthForPicker = (value?: string | null) => {
  if (!value || !/^\d{4}-\d{2}$/.test(value)) {
    return null;
  }
  const parsed = new Date(`${value}-01T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatPeriodoMes = (value: string) => {
  const parsed = parseMonthForPicker(value);
  if (!parsed) return value || "-";
  const label = new Intl.DateTimeFormat("es-PY", {
    month: "long",
    year: "numeric",
  }).format(parsed);
  return label.charAt(0).toUpperCase() + label.slice(1);
};

const formatPeriodoFuente = (value?: string | null) => {
  if (!value) return "-";
  if (/^\d{4}-\d{2}$/.test(value)) {
    return formatPeriodoMes(value);
  }
  return value;
};

const StockCostosOfflinePage = () => {
  const [serverOptions, setServerOptions] = useState<string[]>([]);
  const [serverSeleccionado, setServerSeleccionado] = useState("");
  const [periodo, setPeriodo] = useState(getCurrentMonth());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<ResumenCostoRow[]>([]);
  const [excelDropdownOpen, setExcelDropdownOpen] = useState(false);
  const [pdfDropdownOpen, setPdfDropdownOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [logoDataUrl, setLogoDataUrl] = useState<string | null>(null);
  const [compositionModalOpen, setCompositionModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<ResumenCostoRow | null>(null);
  const [activeFamily, setActiveFamily] = useState<string>("");
  const [compositionRows, setCompositionRows] = useState<DetalleCompraRow[]>([]);
  const [loadingComposition, setLoadingComposition] = useState(false);
  const [compositionError, setCompositionError] = useState<string | null>(null);
  const [purchaseModalOpen, setPurchaseModalOpen] = useState(false);
  const [selectedPurchase, setSelectedPurchase] = useState<DetalleCompraRow | null>(null);

  useEffect(() => {
    document.title = "Stock - Costos MP (Consolidado) | Informes | Saas";
  }, []);

  const loadServers = useCallback(async () => {
    try {
      const response = await requestJson<ServidoresOfflineResponse>(
        "/api/informes/ventas/comparativo-offline/filtros",
      );
      const options = Array.from(
        new Set(
          (response?.servidores ?? [])
            .map((item) => item.trim().toUpperCase())
            .filter((item) => item.length > 0),
        ),
      );
      setServerOptions(options);
      setServerSeleccionado((prev) => {
        if (prev && options.includes(prev)) return prev;
        if (options.includes("PARLA_SERVER")) return "PARLA_SERVER";
        return options[0] ?? "";
      });
    } catch (err) {
      console.error("No se pudieron cargar los servidores:", err);
      setServerOptions([]);
      setServerSeleccionado("");
      setError("No se pudieron cargar los servidores.");
    }
  }, []);

  useEffect(() => {
    void loadServers();
  }, [loadServers]);

  useEffect(() => {
    let active = true;
    const loadLogo = async () => {
      try {
        const sources = [baconLogo, "/baconappen_529x124.png", "/baconappen.png"];
        for (const source of sources) {
          if (!source) continue;
          try {
            const response = await fetch(source);
            if (!response.ok) continue;
            const blob = await response.blob();
            const reader = new FileReader();
            const dataUrl = await new Promise<string>((resolve, reject) => {
              reader.onloadend = () => {
                if (typeof reader.result === "string") {
                  resolve(reader.result);
                } else {
                  reject(new Error("No pudimos leer el logo."));
                }
              };
              reader.onerror = () => reject(new Error("No pudimos leer el logo."));
              reader.readAsDataURL(blob);
            });
            if (active) {
              setLogoDataUrl(dataUrl);
            }
            return;
          } catch {
            continue;
          }
        }
      } catch {
        // noop
      }
    };
    void loadLogo();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!serverSeleccionado || !periodo) {
      setRows([]);
      return;
    }

    let active = true;
    const loadData = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams({
          periodo,
          server: serverSeleccionado,
          tipo: "costo",
        });
        const data = await requestJson<ResumenCostoRow[]>(
          `/api/informes/recetas/resumen?${params.toString()}`,
        );
        if (!active) return;
        setRows(Array.isArray(data) ? data : []);
      } catch (err) {
        if (!active) return;
        console.error("No se pudo cargar el informe de costos:", err);
        setError(
          err instanceof Error
            ? err.message
            : "No se pudo cargar el informe de costos.",
        );
        setRows([]);
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };
    void loadData();

    return () => {
      active = false;
    };
  }, [periodo, serverSeleccionado]);

  const rowFamilyLabel = useCallback((row: ResumenCostoRow) => {
    const familia = String(row.familia ?? "").trim();
    return familia || "Sin familia";
  }, []);

  const familyTabs = useMemo(() => {
    const counts = new Map<string, number>();
    rows.forEach((row) => {
      const familia = rowFamilyLabel(row);
      counts.set(familia, (counts.get(familia) ?? 0) + 1);
    });
    return Array.from(counts.entries())
      .sort((a, b) => a[0].localeCompare(b[0], "es", { sensitivity: "base" }))
      .map(([name, count]) => ({ name, count }));
  }, [rowFamilyLabel, rows]);

  useEffect(() => {
    setActiveFamily((prev) => {
      if (prev && familyTabs.some((tab) => tab.name === prev)) return prev;
      return familyTabs[0]?.name ?? "";
    });
  }, [familyTabs]);

  const visibleRows = useMemo(() => {
    if (!activeFamily) return rows;
    return rows.filter((row) => rowFamilyLabel(row) === activeFamily);
  }, [activeFamily, rowFamilyLabel, rows]);

  const totals = useMemo(
    () =>
      visibleRows.reduce(
        (acc, row) => {
          acc.cantidad += Number(row.cantidadTotal ?? 0);
          acc.sinIva += Number(row.costoPromedioSinIva ?? 0);
          acc.conIva += Number(row.costoPromedioConIva ?? 0);
          return acc;
        },
        { cantidad: 0, sinIva: 0, conIva: 0 },
      ),
    [visibleRows],
  );

  const openCompositionModal = useCallback((row: ResumenCostoRow) => {
    setSelectedRow(row);
    setCompositionModalOpen(true);
  }, []);

  const closeCompositionModal = useCallback(() => {
    setCompositionModalOpen(false);
    setSelectedRow(null);
    setCompositionRows([]);
    setCompositionError(null);
    setLoadingComposition(false);
    setPurchaseModalOpen(false);
    setSelectedPurchase(null);
  }, []);

  const closePurchaseModal = useCallback(() => {
    setPurchaseModalOpen(false);
    setSelectedPurchase(null);
  }, []);

  useEffect(() => {
    if (!compositionModalOpen || !selectedRow?.idArticulo) {
      return;
    }

    let active = true;
    const loadComposition = async () => {
      setLoadingComposition(true);
      setCompositionError(null);
      setCompositionRows([]);
      try {
        const idArticulo = selectedRow.idArticulo;
        if (!idArticulo) {
          setCompositionRows([]);
          return;
        }
        const params = new URLSearchParams({
          periodo,
          server: serverSeleccionado,
          idArticulo,
        });
        const data = await requestJson<DetalleCompraRow[]>(
          `/api/informes/compras/detalle?${params.toString()}`,
        );
        if (!active) return;
        setCompositionRows(Array.isArray(data) ? data : []);
      } catch (err) {
        if (!active) return;
        console.error("No se pudo cargar composición de costo:", err);
        setCompositionError(
          err instanceof Error
            ? err.message
            : "No se pudo cargar la composición de costo.",
        );
      } finally {
        if (active) {
          setLoadingComposition(false);
        }
      }
    };

    void loadComposition();
    return () => {
      active = false;
    };
  }, [compositionModalOpen, periodo, selectedRow, serverSeleccionado]);

  const compositionTotalCantidad = useMemo(
    () =>
      compositionRows.reduce(
        (acc, row) => acc + Number(row.cantidad ?? 0),
        0,
      ),
    [compositionRows],
  );
  const compositionTotalImporte = useMemo(
    () =>
      compositionRows.reduce(
        (acc, row) => acc + Number(row.importeTotal ?? 0),
        0,
      ),
    [compositionRows],
  );
  const compositionCostoUnitPromedioPonderado = useMemo(() => {
    if (compositionTotalCantidad <= 0) return null;
    return compositionTotalImporte / compositionTotalCantidad;
  }, [compositionTotalCantidad, compositionTotalImporte]);
  const compositionPeriodoFuente = useMemo(
    () =>
      compositionRows.find((row) => row.origenMesCosto)?.origenMesCosto ??
      selectedRow?.periodoTexto ??
      null,
    [compositionRows, selectedRow?.periodoTexto],
  );

  const exportDisabled = loading || rows.length === 0;
  type ExportTarget = string | "all";

  const getExportRows = useCallback(
    (target: ExportTarget) => {
      if (target === "all") {
        return {
          rows: rows,
          familyLabel: "Todas",
        };
      }
      return {
        rows: rows.filter((row) => rowFamilyLabel(row) === target),
        familyLabel: target,
      };
    },
    [rowFamilyLabel, rows],
  );

  const toFileSafe = useCallback((value: string) => {
    return value
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }, []);

  const handleExportExcel = useCallback((target: ExportTarget = "all") => {
    if (exportDisabled) return;

    const { rows: exportRows, familyLabel } = getExportRows(target);
    if (exportRows.length === 0) return;

    const workbook = XLSX.utils.book_new();
    const dataRows = exportRows.map((row) => ({
      ID_ARTICULO: row.idArticulo ?? "",
      ARTICULO: row.descripcion ?? "",
      FAMILIA: rowFamilyLabel(row),
      CANTIDAD_TOTAL: Number(row.cantidadTotal ?? 0),
      COSTO_PROMEDIO_SIN_IVA: Number(row.costoPromedioSinIva ?? 0),
      COSTO_PROMEDIO_CON_IVA: Number(row.costoPromedioConIva ?? 0),
      PERIODO_FUENTE: row.periodoTexto ?? "",
      SERVIDOR: serverSeleccionado || "",
      PERIODO: periodo,
    }));
    const exportTotals = exportRows.reduce(
      (acc, row) => {
        acc.cantidad += Number(row.cantidadTotal ?? 0);
        acc.sinIva += Number(row.costoPromedioSinIva ?? 0);
        acc.conIva += Number(row.costoPromedioConIva ?? 0);
        return acc;
      },
      { cantidad: 0, sinIva: 0, conIva: 0 },
    );
    dataRows.push({
      ID_ARTICULO: "",
      ARTICULO: "TOTAL",
      FAMILIA: "",
      CANTIDAD_TOTAL: Number(exportTotals.cantidad ?? 0),
      COSTO_PROMEDIO_SIN_IVA: Number(exportTotals.sinIva ?? 0),
      COSTO_PROMEDIO_CON_IVA: Number(exportTotals.conIva ?? 0),
      PERIODO_FUENTE: "",
      SERVIDOR: serverSeleccionado || "",
      PERIODO: periodo,
    });

    const sheet = XLSX.utils.json_to_sheet(dataRows);
    XLSX.utils.book_append_sheet(workbook, sheet, "CostosMP");

    const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
    const targetLabel = target === "all" ? "todas" : familyLabel;
    XLSX.writeFile(
      workbook,
      `stock-costos-mp-${toFileSafe(serverSeleccionado || "server")}-${toFileSafe(periodo)}-${toFileSafe(targetLabel)}-${timestamp}.xlsx`,
    );
  }, [
    exportDisabled,
    getExportRows,
    periodo,
    rowFamilyLabel,
    serverSeleccionado,
    toFileSafe,
  ]);

  const handleExportPdf = useCallback((target: ExportTarget = "all") => {
    if (exportDisabled) return;

    const { rows: exportRows, familyLabel } = getExportRows(target);
    if (exportRows.length === 0) return;

    const doc = new jsPDF({
      orientation: "landscape",
      unit: "pt",
      format: "a4",
    });

    const leftX = 40;
    const rightX = 20;
    const pageWidth = doc.internal.pageSize.getWidth();
    const infoRightX = pageWidth - rightX;
    const logoWidth = 110;
    const logoHeight = 24;
    const headerTopY = 24;
    const titleY = logoDataUrl ? headerTopY + logoHeight + 10 : headerTopY + 16;
    const infoStartY = headerTopY + 4;
    const infoRowHeight = 11;
    const infoLines = [
      { label: "Servidor", value: serverSeleccionado || "-" },
      { label: "Período", value: periodo },
      { label: "Familia", value: familyLabel || "-" },
      { label: "Registros", value: String(exportRows.length) },
    ];
    const infoBottomY = infoStartY + (infoLines.length - 1) * infoRowHeight + 2;
    const titleBottomY = titleY + 35;
    const headerHeight = Math.max(infoBottomY, titleBottomY) + 10;

    const fechaHora = new Date().toLocaleString("es-PY");
    const usuario = resolveUsuarioActual();

    const renderHeader = () => {
      if (logoDataUrl) {
        doc.addImage(logoDataUrl, "PNG", leftX, headerTopY - 4, logoWidth, logoHeight);
      }
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(14);
      doc.text("Stock - Costos MP", leftX, titleY);
      doc.setFontSize(8);
      doc.setTextColor(0, 0, 0);
      doc.text("Generado: ", leftX, titleY + 13);
      doc.setTextColor(120, 120, 120);
      doc.text(fechaHora, leftX + doc.getTextWidth("Generado: "), titleY + 13);
      doc.setTextColor(0, 0, 0);
      doc.text("Usuario: ", leftX, titleY + 24);
      doc.setTextColor(120, 120, 120);
      doc.text(usuario, leftX + doc.getTextWidth("Usuario: "), titleY + 24);

      infoLines.forEach((line, index) => {
        const y = infoStartY + index * infoRowHeight;
        const label = `${line.label}: `;
        const labelWidth = doc.getTextWidth(label);
        const valueWidth = doc.getTextWidth(line.value);
        const startX = infoRightX - (labelWidth + valueWidth);
        doc.setTextColor(0, 0, 0);
        doc.text(label, startX, y);
        doc.setTextColor(120, 120, 120);
        doc.text(line.value, startX + labelWidth, y);
      });
      doc.setTextColor(0, 0, 0);
    };

    const body = exportRows.map((row) => [
      `${row.idArticulo ?? "-"} · ${row.descripcion ?? "Sin descripción"}`,
      numberFormat.format(Number(row.cantidadTotal ?? 0)),
      costFormat.format(Number(row.costoPromedioSinIva ?? 0)),
      costFormat.format(Number(row.costoPromedioConIva ?? 0)),
      row.periodoTexto || "-",
    ]);
    const exportTotals = exportRows.reduce(
      (acc, row) => {
        acc.cantidad += Number(row.cantidadTotal ?? 0);
        acc.sinIva += Number(row.costoPromedioSinIva ?? 0);
        acc.conIva += Number(row.costoPromedioConIva ?? 0);
        return acc;
      },
      { cantidad: 0, sinIva: 0, conIva: 0 },
    );
    body.push([
      { content: "TOTAL", styles: { fontStyle: "bold", fillColor: [33, 37, 41], textColor: [255, 255, 255] } },
      { content: numberFormat.format(Number(exportTotals.cantidad ?? 0)), styles: { fontStyle: "bold", fillColor: [33, 37, 41], textColor: [255, 255, 255] } },
      { content: costFormat.format(Number(exportTotals.sinIva ?? 0)), styles: { fontStyle: "bold", fillColor: [33, 37, 41], textColor: [255, 255, 255] } },
      { content: costFormat.format(Number(exportTotals.conIva ?? 0)), styles: { fontStyle: "bold", fillColor: [33, 37, 41], textColor: [255, 255, 255] } },
      { content: "", styles: { fontStyle: "bold", fillColor: [33, 37, 41], textColor: [255, 255, 255] } },
    ] as any);

    autoTable(doc, {
      head: [["Artículo", "Cantidad total", "Costo promedio sin IVA", "Costo promedio con IVA", "Período fuente"]],
      body,
      startY: headerHeight + 16,
      margin: { left: leftX, right: rightX, top: headerHeight },
      theme: "grid",
      styles: { fontSize: 7, cellPadding: 3 },
      headStyles: { fillColor: [33, 37, 41] },
      columnStyles: {
        0: { halign: "left" },
        1: { halign: "right" },
        2: { halign: "right" },
        3: { halign: "right" },
        4: { halign: "left" },
      },
      didDrawPage: () => {
        renderHeader();
      },
    });

    const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
    const targetLabel = target === "all" ? "todas" : familyLabel;
    doc.save(
      `stock-costos-mp-${toFileSafe(serverSeleccionado || "server")}-${toFileSafe(periodo)}-${toFileSafe(targetLabel)}-${timestamp}.pdf`,
    );
  }, [
    costFormat,
    exportDisabled,
    getExportRows,
    logoDataUrl,
    numberFormat,
    periodo,
    serverSeleccionado,
    toFileSafe,
  ]);

  return (
    <div className="page-content">
      <Container fluid className="px-2 px-md-3">
        <BreadCrumb title="Stock - Costos MP" pageTitle="Informes" />
        <Card className="border-0 shadow-sm">
          <CardHeader className="bg-transparent">
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
              <div>
                <h5 className="mb-1">Costos MP</h5>
                <p className="text-muted mb-0">
                  Datos consolidados de costos promedio por artículo.
                </p>
              </div>
              <div className="d-flex align-items-center gap-2">
                <Dropdown
                  isOpen={excelDropdownOpen}
                  toggle={() => setExcelDropdownOpen((prev) => !prev)}
                >
                  <DropdownToggle
                    color="dark"
                    className="btn-soft-dark"
                    caret
                    disabled={exportDisabled}
                  >
                    <i className="ri-file-excel-2-line me-1"></i>
                    Excel
                  </DropdownToggle>
                  <DropdownMenu end>
                    {familyTabs.map((tab) => (
                      <DropdownItem key={`excel-${tab.name}`} onClick={() => handleExportExcel(tab.name)}>
                        <i className="ri-file-list-3-line me-2"></i>
                        {tab.name} ({tab.count})
                      </DropdownItem>
                    ))}
                    {familyTabs.length > 0 ? <DropdownItem divider /> : null}
                    <DropdownItem onClick={() => handleExportExcel("all")}>
                      <i className="ri-file-copy-2-line me-2"></i>
                      Todas
                    </DropdownItem>
                  </DropdownMenu>
                </Dropdown>
                <Dropdown
                  isOpen={pdfDropdownOpen}
                  toggle={() => setPdfDropdownOpen((prev) => !prev)}
                >
                  <DropdownToggle
                    color="dark"
                    className="btn-soft-dark"
                    caret
                    disabled={exportDisabled}
                  >
                    <i className="ri-file-pdf-2-line me-1"></i>
                    PDF
                  </DropdownToggle>
                  <DropdownMenu end>
                    {familyTabs.map((tab) => (
                      <DropdownItem key={`pdf-${tab.name}`} onClick={() => handleExportPdf(tab.name)}>
                        <i className="ri-file-list-3-line me-2"></i>
                        {tab.name} ({tab.count})
                      </DropdownItem>
                    ))}
                    {familyTabs.length > 0 ? <DropdownItem divider /> : null}
                    <DropdownItem onClick={() => handleExportPdf("all")}>
                      <i className="ri-file-copy-2-line me-2"></i>
                      Todas
                    </DropdownItem>
                  </DropdownMenu>
                </Dropdown>
                <Button
                  color="dark"
                  className="btn-soft-dark"
                  onClick={() => setFiltersOpen(true)}
                >
                  <i className="ri-equalizer-line me-1"></i>
                  Filtros
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardBody>
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
              <div className="text-muted small">
                Servidor: {serverSeleccionado || "-"} · Período:{" "}
                {formatPeriodoMes(periodo)} · Registros: {visibleRows.length}/{rows.length}
              </div>
            </div>

            {error ? <Alert color="danger">{error}</Alert> : null}
            {loading ? (
              <div className="text-muted py-4">
                <Spinner size="sm" className="me-2" />
                Cargando costos...
              </div>
            ) : (
              <>
                {familyTabs.length > 0 ? (
                  <Nav pills className="mb-3 flex-wrap gap-2">
                    {familyTabs.map((tab) => (
                      <NavItem key={tab.name}>
                        <NavLink
                          href="#"
                          active={activeFamily === tab.name}
                          onClick={(event) => {
                            event.preventDefault();
                            setActiveFamily(tab.name);
                          }}
                          className="py-1 px-2"
                        >
                          {tab.name} <span className="text-muted">({tab.count})</span>
                        </NavLink>
                      </NavItem>
                    ))}
                  </Nav>
                ) : null}

                <div className="table-responsive">
                  <Table hover className="align-middle mb-0 table-nowrap">
                    <thead className="table-light">
                      <tr>
                        <th>Artículo</th>
                        <th className="text-end">Cantidad total</th>
                        <th className="text-end">Costo promedio sin IVA</th>
                        <th className="text-end">Costo promedio con IVA</th>
                        <th>Período fuente</th>
                        <th className="text-center">ACC.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleRows.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="text-center text-muted py-4">
                            Sin datos para los filtros seleccionados.
                          </td>
                        </tr>
                      ) : (
                        visibleRows.map((row, index) => (
                          <tr key={`${row.idArticulo ?? "sin-id"}-${index}`}>
                            <td>
                              <div className="d-flex align-items-center gap-2 text-nowrap">
                                <span className="badge bg-primary-subtle text-primary">
                                  {row.idArticulo ?? "SIN ID"}
                                </span>
                                <span
                                  className="fw-semibold text-truncate"
                                  style={{ maxWidth: 420 }}
                                  title={row.descripcion ?? "Sin descripción"}
                                >
                                  {row.descripcion ?? "Sin descripción"}
                                </span>
                              </div>
                            </td>
                            <td className="text-end">
                              {numberFormat.format(Number(row.cantidadTotal ?? 0))}
                            </td>
                            <td className="text-end">
                              {costFormat.format(Number(row.costoPromedioSinIva ?? 0))}
                            </td>
                            <td className="text-end">
                              {costFormat.format(Number(row.costoPromedioConIva ?? 0))}
                            </td>
                            <td>{row.periodoTexto || "-"}</td>
                            <td className="text-center">
                              <div className="d-flex justify-content-center">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-soft-primary"
                                  title="Ver composición de costo"
                                  onClick={() => openCompositionModal(row)}
                                >
                                  <i className="ri-search-eye-line"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                    <tfoot>
                      <tr className="table-light">
                        <th>Total</th>
                        <th className="text-end">{numberFormat.format(totals.cantidad)}</th>
                        <th className="text-end">{costFormat.format(totals.sinIva)}</th>
                        <th className="text-end">{costFormat.format(totals.conIva)}</th>
                        <th />
                        <th className="text-center text-muted">-</th>
                      </tr>
                    </tfoot>
                  </Table>
                </div>
              </>
            )}
          </CardBody>
        </Card>
      </Container>

      <Offcanvas
        isOpen={filtersOpen}
        toggle={() => setFiltersOpen(false)}
        direction="end"
        className="border-start"
      >
        <OffcanvasHeader toggle={() => setFiltersOpen(false)}>
          Filtros
        </OffcanvasHeader>
        <OffcanvasBody>
          <FormGroup className="mb-3">
            <Label className="form-label mb-1">Servidor</Label>
            <Input
              type="select"
              value={serverSeleccionado}
              onChange={(event) => setServerSeleccionado(event.target.value)}
            >
              {serverOptions.length === 0 ? (
                <option value="">Sin servidores</option>
              ) : null}
              {serverOptions.map((server) => (
                <option key={server} value={server}>
                  {server}
                </option>
              ))}
            </Input>
          </FormGroup>

          <FormGroup className="mb-3">
            <Label className="form-label mb-1">Período</Label>
            <Flatpickr
              className="form-control"
              value={parseMonthForPicker(periodo) ? [parseMonthForPicker(periodo)!] : []}
              options={{
                mode: "single",
                altInput: true,
                plugins: [
                  createMonthSelectPlugin({
                    shorthand: true,
                    dateFormat: "Y-m",
                    altFormat: "F Y",
                  }),
                ],
              }}
              onChange={(dates: Date[]) => {
                if (!dates || dates.length === 0 || !dates[0]) {
                  return;
                }
                const selected = dates[0];
                const value = `${selected.getFullYear()}-${String(
                  selected.getMonth() + 1,
                ).padStart(2, "0")}`;
                setPeriodo(value);
              }}
            />
          </FormGroup>

          <Button
            color="dark"
            className="btn-soft-dark w-100"
            onClick={() => setFiltersOpen(false)}
          >
            Aplicar
          </Button>
        </OffcanvasBody>
      </Offcanvas>

      <Modal isOpen={compositionModalOpen} toggle={closeCompositionModal} size="xl" centered>
        <ModalHeader toggle={closeCompositionModal}>
          Composición de costo · {selectedRow?.descripcion ?? "Sin artículo"}
        </ModalHeader>
        <ModalBody>
          {!selectedRow ? null : (
            <>
              <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                <small className="text-muted">
                  <strong>Artículo:</strong> {selectedRow.idArticulo ?? "-"}
                </small>
                <small className="text-muted">
                  <strong>Cantidad:</strong> {numberFormat.format(selectedRow.cantidadTotal ?? 0)}
                </small>
                <span className="badge rounded-pill bg-warning-subtle text-warning">
                  Costo sin IVA: {costFormat.format(selectedRow.costoPromedioSinIva ?? 0)}
                </span>
                <span className="badge rounded-pill bg-success-subtle text-success">
                  Costo con IVA: {costFormat.format(selectedRow.costoPromedioConIva ?? 0)}
                </span>
                <small className="text-muted">
                  <strong>Período fuente:</strong> {formatPeriodoFuente(compositionPeriodoFuente)}
                </small>
                <small className="text-muted">
                  <strong>Servidor:</strong> {serverSeleccionado || "-"}
                </small>
              </div>

              {compositionError ? <Alert color="danger">{compositionError}</Alert> : null}

              <div className="table-responsive">
                <Table hover className="align-middle mb-0 table-nowrap">
                  <thead className="table-light">
                    <tr>
                      <th>Fecha</th>
                      <th>Factura</th>
                      <th className="text-end">Cantidad</th>
                      <th className="text-end">Costo U.</th>
                      <th className="text-end">Importe</th>
                      <th>Depósito</th>
                      <th>Proveedor</th>
                      <th>Período fuente</th>
                      <th className="text-center">ACC.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingComposition ? (
                      <tr>
                        <td className="text-muted" colSpan={9}>
                          <Spinner size="sm" className="me-2" />
                          Cargando composición...
                        </td>
                      </tr>
                    ) : compositionRows.length === 0 ? (
                      <tr>
                        <td className="text-muted" colSpan={9}>
                          Sin compras históricas para este artículo.
                        </td>
                      </tr>
                    ) : (
                      compositionRows.map((row, index) => (
                        <tr
                          key={`${row.fecha ?? "sin-fecha"}-${row.factura ?? "sin-fac"}-${index}`}
                        >
                          <td>{row.fecha ?? "-"}</td>
                          <td>{row.nroFactura || (row.factura != null ? String(row.factura) : "-")}</td>
                          <td className="text-end">
                            {quantityFormat.format(Number(row.cantidad ?? 0))}
                          </td>
                          <td className="text-end">
                            {costFormat.format(Number(row.costoUnitConIva ?? 0))}
                          </td>
                          <td className="text-end">
                            {costFormat.format(Number(row.importeTotal ?? 0))}
                          </td>
                          <td>{row.deposito ?? "-"}</td>
                          <td>{row.persona ?? "-"}</td>
                          <td>{formatPeriodoFuente(row.origenMesCosto ?? compositionPeriodoFuente)}</td>
                          <td className="text-center">
                            <div className="d-flex justify-content-center">
                              <button
                                type="button"
                                className="btn btn-sm btn-soft-primary"
                                title="Ver detalle de compra"
                                onClick={() => {
                                  setSelectedPurchase(row);
                                  setPurchaseModalOpen(true);
                                }}
                              >
                                <i className="ri-search-eye-line"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                  <tfoot>
                    <tr className="table-light">
                      <th colSpan={2}>Total</th>
                      <th className="text-end">
                        {compositionRows.length > 0
                          ? quantityFormat.format(Number(compositionTotalCantidad ?? 0))
                          : "-"}
                      </th>
                      <th className="text-end">
                        {compositionRows.length > 0 &&
                        compositionCostoUnitPromedioPonderado != null
                          ? costFormat.format(Number(compositionCostoUnitPromedioPonderado ?? 0))
                          : "-"}
                      </th>
                      <th className="text-end">
                        {compositionRows.length > 0
                          ? costFormat.format(Number(compositionTotalImporte ?? 0))
                          : "-"}
                      </th>
                      <th colSpan={2} className="text-muted small">
                        Costo U. prom. pond.
                      </th>
                      <th>{formatPeriodoFuente(compositionPeriodoFuente)}</th>
                      <th className="text-center text-muted">-</th>
                    </tr>
                  </tfoot>
                </Table>
              </div>

              <div className="d-flex justify-content-end mt-3">
                <Button color="dark" className="btn-soft-dark" onClick={closeCompositionModal}>
                  Cerrar
                </Button>
              </div>
            </>
          )}
        </ModalBody>
      </Modal>

      <Modal isOpen={purchaseModalOpen} toggle={closePurchaseModal} size="lg" centered>
        <ModalHeader toggle={closePurchaseModal}>
          Detalle de compra · {selectedRow?.descripcion ?? "Sin artículo"}
        </ModalHeader>
        <ModalBody>
          {!selectedPurchase ? null : (
            <>
              <div className="d-flex flex-wrap gap-2 mb-3 text-muted">
                <small>
                  <strong>Factura:</strong>{" "}
                  {selectedPurchase.nroFactura || selectedPurchase.factura || "-"}
                </small>
                <small>
                  <strong>Fecha:</strong> {selectedPurchase.fecha ?? "-"}
                </small>
                <small>
                  <strong>Período fuente:</strong>{" "}
                  {formatPeriodoFuente(selectedPurchase.origenMesCosto)}
                </small>
              </div>

              <div className="table-responsive">
                <Table hover className="align-middle mb-0 table-nowrap">
                  <tbody>
                    <tr>
                      <th style={{ width: 220 }}>Cantidad</th>
                      <td className="text-end">
                        {quantityFormat.format(Number(selectedPurchase.cantidad ?? 0))}
                      </td>
                    </tr>
                    <tr>
                      <th>Costo U. sin IVA</th>
                      <td className="text-end">
                        {costFormat.format(Number(selectedPurchase.costoUnitSinIva ?? 0))}
                      </td>
                    </tr>
                    <tr>
                      <th>Costo U. con IVA</th>
                      <td className="text-end">
                        {costFormat.format(Number(selectedPurchase.costoUnitConIva ?? 0))}
                      </td>
                    </tr>
                    <tr>
                      <th>Importe</th>
                      <td className="text-end">
                        {costFormat.format(Number(selectedPurchase.importeTotal ?? 0))}
                      </td>
                    </tr>
                    <tr>
                      <th>Depósito</th>
                      <td>{selectedPurchase.deposito ?? "-"}</td>
                    </tr>
                    <tr>
                      <th>Proveedor</th>
                      <td>{selectedPurchase.persona ?? "-"}</td>
                    </tr>
                    <tr>
                      <th>Condición</th>
                      <td>{selectedPurchase.condicion ?? "-"}</td>
                    </tr>
                    <tr>
                      <th>Tipo mov.</th>
                      <td>{selectedPurchase.tipoMovimiento ?? "-"}</td>
                    </tr>
                    <tr>
                      <th>Fecha carga</th>
                      <td>{selectedPurchase.fechaCarga ?? "-"}</td>
                    </tr>
                    <tr>
                      <th>Usuario</th>
                      <td>{selectedPurchase.usuario ?? "-"}</td>
                    </tr>
                  </tbody>
                </Table>
              </div>

              <div className="d-flex justify-content-end mt-3">
                <Button color="dark" className="btn-soft-dark" onClick={closePurchaseModal}>
                  Cerrar
                </Button>
              </div>
            </>
          )}
        </ModalBody>
      </Modal>
    </div>
  );
};

export default StockCostosOfflinePage;
